import { Component,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewEncapsulation, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SourcingAmountsService } from '../../services/sourcing-amounts.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { SourcingAmountsObj, SourcingAmountsRow } from '../../models/sourcing-amounts.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-sourcing-amounts',
    templateUrl: './sourcing-amounts.component.html',
    styleUrls: ['./sourcing-amounts.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SourcingAmountsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('cancel-sourcing-amts') cancelSourcingAmts = new EventEmitter<string>();
  @Output('save-sourcing-amts') saveSourcingAmts = new EventEmitter<string>();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  GENERAL_CONFIG: any = {};
  globalParams: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  private baseURL = '/gtw';
  private checkLockedObj: any = [];
  private taxYear: number = 0;
  private scenario: number = 0;
  private jcdKey: number = 0;
  private ssoId: string = '';
  private combinationKey: number = 0;
  private processName: string = '';
  private scenarioCode: string = '';
  private isIssueKeyEnabled: string = '';
  private isLocked: string = '';
  private issueKey: number = 0;

  clob_settings: { tax_year: number; scenario: number; jcd_key: number; sso_id: string; combination_key: number; } = {
    tax_year: 0,
    scenario: 0,
    jcd_key: 0,
    sso_id: '',
    combination_key: 0,
  };
  clob_data: SourcingAmountsRow[] = [];
  sourcingAmountsData: SourcingAmountsRow[] = [];

  public finalData: SourcingAmountsObj = {};

  public validationErrMsg: string = '';

  isLoading = false;
  isSaving = false;
  minimise = false;
  modalTitle = 'Edit Sourcing Amounts';
  firstRow: SourcingAmountsRow[] = [];

  objectKeys = Object.keys;

  constructor(
    private sourcingAmtsService: SourcingAmountsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }
  checkInputType(): boolean {
    if (Number.isInteger(parseInt(this.taxYear + "")) &&
      Number.isInteger(parseInt(this.scenario + '')) &&
      Number.isInteger(parseInt(this.jcdKey + '')) &&
      Number.isInteger(parseInt(this.combinationKey + '')) &&
      Number.isInteger(parseInt(this.issueKey + '')) &&
      (!this.scenarioCode.includes("{{")) &&   // this.scenarioCode === "RAF" || this.scenarioCode === "EXAM"
      (!this.isIssueKeyEnabled.includes("{{")) // this.isIssueKeyEnabled == "Y" || this.isIssueKeyEnabled == "N"
    ) {
      return true;
    }

    return false;
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const activeData = activeScreen?.data || {};

    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);

    this.baseURL = this.GENERAL_CONFIG.api || '/gtw';
    this.taxYear = this.globalParams?.tax_year || this.filterParams?.tax_year || 0;
    this.scenario = this.globalParams?.scenario || this.filterParams?.scenario || 0;
    this.jcdKey = this.globalParams?.jcd_key || this.filterParams?.jcd_key || 0;
    this.ssoId = this.globalParams?.sso_id || this.USER_SETTINGS?.user?.sso_id || '';
    this.combinationKey = this.rowData?.COMBINATION_KEY || this.filterParams?.combination_key || 0;
    this.scenarioCode = this.globalParams?.scenarioCode || this.globalParams?.scenario_code || this.filterParams?.scenarioCode || '';
    this.isIssueKeyEnabled = this.globalParams?.is_issue_key_enabled || 'N';
    this.issueKey = this.globalParams?.issue_id || this.filterParams?.issue_key || 0;
    this.checkLockedObj = activeData?.checkLockedObj || activeData?.check_locked_obj || [];
    this.isLocked = activeData?.is_locked || this.filterParams?.is_locked || 'N';
    this.isSaving = this.isLocked === 'Y';

    const scenarioLabel = this.filterParams?.scenarioLabel || this.filterParams?.scenario_label
      || this.globalParams?.scenario_desc || this.globalParams?.scenarioLabel || 'Return As Filed';
    this.modalTitle = `Edit Sourcing Amounts ( Tax Year : ${this.taxYear} - Scenario : ${scenarioLabel} )`;

    this.cdr.markForCheck();
  }

  init() {
    this.isLoading = true;
    if (this.checkInputType()) {
      this.getModalData();
    }
  }

  groupData(): void {
    let index = 0;
    this.sourcingAmountsData.forEach((data: SourcingAmountsRow) => {
      let currentKey = data.PTR_LE;
      if (currentKey in this.finalData) {
        this.finalData[data.PTR_LE].push({ ...data, IS_DIRTY: 'N', INDEX: index });
      }
      else {
        ++index;
        this.finalData[currentKey] = [{ ...data, IS_DIRTY: 'N', INDEX: index, display: true }];

      }
    })
  }

  getModalData(): void {
    console.log('Get Sourcing Amts Popup Data Called.');
    if (this.sourcingAmountsData.length == 0) {

      this.isLoading = true;

      this.sourcingAmtsService.getSourcingPopupData(this.baseURL, this.taxYear, this.scenario, this.jcdKey, this.combinationKey, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: SourcingAmountsRow[]) => {
        if (data && data.length > 0) {
          this.sourcingAmountsData = data;
          this.firstRow = [this.sourcingAmountsData[0]];
          this.firstRow[0].TAX_YEAR_BEGIN = this.getFormattedDate(new Date(this.firstRow[0].TAX_YEAR_BEGIN));
          this.firstRow[0].TAX_YEAR_END = this.getFormattedDate(new Date(this.firstRow[0].TAX_YEAR_END));
          this.groupData();
          console.log("final table data:  ", this.finalData)
        }
        else {
          this.sourcingAmtsService.openSnackBar('No records found!', 'gtw-snackbar--red');
        }


      }, error => {
        this.isLoading = false;
        console.log(error);
      }, () => {
        this.isLoading = false;
        console.log('Completed all tabs');
      })
    }
  }

  expandCollapse(key: string): void {
    this.finalData[key][0].display = !this.finalData[key][0].display;
  }

  getFormattedDate(date: Date): string {
    var year = date.getFullYear();

    var month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : '0' + month;

    var day = date.getDate().toString();
    day = day.length > 1 ? day : '0' + day;

    return month + '/' + day + '/' + year;
  }

  updateBasket(event: any, val: string, body: SourcingAmountsRow) {
    if (!isNaN(+val)) {
      body.BRANCH = parseInt(body.PTR_AMT_PSHIP_CURR) - parseInt(body.GENLIM) - parseInt(body.PASSIVE) - parseInt(body.TREATY) - parseInt(body['901j']);
      body.IS_DIRTY = 'Y';
    }
  }

  cancel(): void {
    this.cancelSourcingAmts.emit('cancel-sourcing-amts');
    this.requestGridUpdate.emit('cancel');
  }
  saveEntries(): void {
    this.isLoading = true;
    this.clob_data = [];
    this.clob_settings = {
      tax_year: this.taxYear,
      scenario: this.scenario,
      jcd_key: this.jcdKey,
      sso_id: this.ssoId,
      combination_key: this.combinationKey,
    };
    let isClean = true;
    Object.values(this.finalData).forEach((partner: any) => {
      partner.forEach((account: SourcingAmountsRow) => {
        if (account.IS_DIRTY == 'Y') {
          if (isNaN(+account.BRANCH) || isNaN(+account.GENLIM) || isNaN(+account.PASSIVE) || isNaN(+account.TREATY) || isNaN(+account['901j'])) {
            isClean = false;
          } else {
            this.clob_data.push({
              ...account,
              basket: [
                {
                  name: 'BRANCH', value: account.BRANCH
                },
                {
                  name: 'GENLIM', value: account.GENLIM
                },
                {
                  name: 'PASSIVE', value: account.PASSIVE
                },
                {
                  name: 'TREATY', value: account.TREATY
                },
                {
                  name: '901j', value: account['901j']
                }
              ]
            })
          }
          
        }

      })
    });
    if (this.clob_data.length > 0 && isClean) {
      this.sourcingAmtsService.saveSplAllocationData(this.baseURL, this.taxYear, this.scenario, this.jcdKey, this.ssoId, this.processName, this.checkLockedObj, this.scenarioCode, this.isIssueKeyEnabled, this.issueKey, this.clob_settings, this.clob_data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: any) => {
        this.isLoading = false;
        this.isSaving = false;
        if (response.callSuccess == "1") {
          this.sourcingAmtsService.openSnackBar('Sourcing Amounts Saved', 'gtw-snackbar--green');
          this.saveSourcingAmts.emit('save-sourcing-amts');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.sourcingAmtsService.openSnackBar('Error Saving Sourcing Data. ' + response.errorMessage, 'gtw-snackbar--red');
        }
      });
    }
    else {
      this.sourcingAmtsService.openSnackBar(isClean ? 'No value changed!' : 'Please enter correct number values', 'gtw-snackbar--red');
      this.isLoading = false;
      this.isSaving = false;
    }

  }

}
